/**
 * InvestableAssetsForm — Screen 2: Investable Assets
 *
 * Pure form component (no layout). Renders cash, registered accounts,
 * and non-registered investments. Separated from the Real Estate form
 * to distinguish liquid portfolio from property/physical assets.
 *
 * Used by mobile SettingsPageLayout and desktop SettingsModal.
 */

import CurrencyInput from "../components/CurrencyInput";
import { SectionHeader } from "../components/SectionHeader";
import { MarketStatistic } from "../components/MarketStatistic";
import { useInvestableAssetsLogic } from "./InvestableAssets.logic";
import { useFieldStatistic } from "../hooks/useStatistics";
import { useStatisticsValue } from "../hooks/useStatisticsValue";

/**
 * InvestableAssetsForm - Pure form component (no layout)
 *
 * Displays all investable asset fields organized by section:
 * - Cash & Liquid Assets (essential)
 * - Registered Accounts (essential + advanced)
 * - Non-Registered Investments (essential)
 * - Pension & Employer Plans (advanced)
 */
export function InvestableAssetsForm() {
  const { formData, handleChange, handleBlur, loading, totalInvestableAssets } =
    useInvestableAssetsLogic();

  // Statistic values for investable assets category
  const assetsStatistic = useStatisticsValue("investableAssets");

  // Help text from statistics or fallback descriptions
  const rrspHelp = useFieldStatistic(
    "rrspBalance",
    "Registered Retirement Savings Plan"
  );
  const tfsaHelp = useFieldStatistic("tfsaBalance", "Tax-Free Savings Account");
  const nonRegHelp = useFieldStatistic(
    "nonRegisteredInvestments",
    "Stocks, bonds, ETFs, mutual funds outside registered accounts"
  );
  const cashHelp = useFieldStatistic(
    "cashSavings",
    "Money in checking and savings accounts"
  );
  const emergencyHelp = useFieldStatistic(
    "emergencyFund",
    "Liquid funds set aside for unexpected expenses"
  );
  const spousalRrspHelp = useFieldStatistic(
    "spousalRrspBalance",
    "Spousal RRSP balance (contributed by you or your spouse)"
  );
  const fhsaHelp = useFieldStatistic(
    "fhsaBalance",
    "First Home Savings Account — tax-free savings for first home"
  );
  const respHelp = useFieldStatistic(
    "respBalance",
    "Registered Education Savings Plan"
  );
  const liraHelp = useFieldStatistic(
    "liraBalance",
    "Locked-In Retirement Account or LRSP"
  );
  const rrifHelp = useFieldStatistic(
    "rrifBalance",
    "Registered Retirement Income Fund or LIF"
  );
  const dpspHelp = useFieldStatistic(
    "dpspBalance",
    "Deferred Profit Sharing Plan"
  );
  const gicHelp = useFieldStatistic(
    "gicTermDeposits",
    "GICs, term deposits, and other fixed-term instruments"
  );
  const dbPensionHelp = useFieldStatistic(
    "dbPensionCommutedValue",
    "Defined Benefit pension — lump sum commuted value"
  );
  const dcPensionHelp = useFieldStatistic(
    "dcPensionBalance",
    "Defined Contribution pension balance"
  );
  const groupRrspHelp = useFieldStatistic(
    "groupRrspBalance",
    "Employer group RRSP balance"
  );
  const stockOptionsHelp = useFieldStatistic(
    "stockOptionsRsusValue",
    "Vested stock options, RSUs, ESPP — current market value"
  );

  if (loading) {
    return null;
  }

  return (
    <div className="settings-form-container" id="investable-assets-form">
      <form className="settings-form-scroll">
        {/* Section: Cash & Liquid Assets */}
        <SectionHeader title="Cash & Liquid Assets" sectionId="cash" />
        <div className="form-grid">
          <CurrencyInput
            name="cashSavings"
            value={formData.cashSavings}
            onChange={handleChange}
            onBlur={handleBlur}
            label="Cash & Savings"
            helpText={cashHelp}
          />
          <CurrencyInput
            name="emergencyFund"
            value={formData.emergencyFund}
            onChange={handleChange}
            onBlur={handleBlur}
            label="Emergency Fund"
            helpText={emergencyHelp}
          />
          <CurrencyInput
            name="gicTermDeposits"
            value={formData.gicTermDeposits}
            onChange={handleChange}
            onBlur={handleBlur}
            label="GIC & Term Deposits"
            helpText={gicHelp}
          />
        </div>

        {/* Section: Registered Accounts */}
        <SectionHeader title="Registered Accounts" sectionId="registered" />
        <div className="form-grid">
          <CurrencyInput
            name="rrspBalance"
            value={formData.rrspBalance}
            onChange={handleChange}
            onBlur={handleBlur}
            label="RRSP Balance"
            helpText={rrspHelp}
          />
          <CurrencyInput
            name="spousalRrspBalance"
            value={formData.spousalRrspBalance}
            onChange={handleChange}
            onBlur={handleBlur}
            label="Spousal RRSP Balance"
            helpText={spousalRrspHelp}
          />
          <CurrencyInput
            name="tfsaBalance"
            value={formData.tfsaBalance}
            onChange={handleChange}
            onBlur={handleBlur}
            label="TFSA Balance"
            helpText={tfsaHelp}
          />
          <CurrencyInput
            name="fhsaBalance"
            value={formData.fhsaBalance}
            onChange={handleChange}
            onBlur={handleBlur}
            label="FHSA Balance"
            helpText={fhsaHelp}
          />
          <CurrencyInput
            name="respBalance"
            value={formData.respBalance}
            onChange={handleChange}
            onBlur={handleBlur}
            label="RESP Balance"
            helpText={respHelp}
          />
          <CurrencyInput
            name="liraBalance"
            value={formData.liraBalance}
            onChange={handleChange}
            onBlur={handleBlur}
            label="LIRA Balance"
            helpText={liraHelp}
          />
          <CurrencyInput
            name="rrifBalance"
            value={formData.rrifBalance}
            onChange={handleChange}
            onBlur={handleBlur}
            label="RRIF Balance"
            helpText={rrifHelp}
          />
          <CurrencyInput
            name="dpspBalance"
            value={formData.dpspBalance}
            onChange={handleChange}
            onBlur={handleBlur}
            label="DPSP Balance"
            helpText={dpspHelp}
          />
        </div>

        {/* Section: Non-Registered Investments */}
        <SectionHeader
          title="Non-Registered Investments"
          sectionId="non-registered"
        />
        <div className="form-grid">
          <CurrencyInput
            name="nonRegisteredInvestments"
            value={formData.nonRegisteredInvestments}
            onChange={handleChange}
            onBlur={handleBlur}
            label="Non-Registered Investments"
            helpText={nonRegHelp}
          />
        </div>

        {/* Section: Pension & Employer Plans */}
        <SectionHeader title="Pension & Employer Plans" sectionId="pension" />
        <div className="form-grid">
          <CurrencyInput
            name="dbPensionCommutedValue"
            value={formData.dbPensionCommutedValue}
            onChange={handleChange}
            onBlur={handleBlur}
            label="DB Pension Commuted Value"
            helpText={dbPensionHelp}
          />
          <CurrencyInput
            name="dcPensionBalance"
            value={formData.dcPensionBalance}
            onChange={handleChange}
            onBlur={handleBlur}
            label="DC Pension Balance"
            helpText={dcPensionHelp}
          />
          <CurrencyInput
            name="groupRrspBalance"
            value={formData.groupRrspBalance}
            onChange={handleChange}
            onBlur={handleBlur}
            label="Group RRSP Balance"
            helpText={groupRrspHelp}
          />
          <CurrencyInput
            name="stockOptionsRsusValue"
            value={formData.stockOptionsRsusValue}
            onChange={handleChange}
            onBlur={handleBlur}
            label="Stock Options & RSUs"
            helpText={stockOptionsHelp}
          />
        </div>
      </form>

      {/* Total (calculated, read-only) - Sticky at bottom for long forms */}
      <div className="form-sticky-save" id="assets-total-bar">
        <div className="form-total-statistic-spacing">
          <MarketStatistic
            label="Canadian Investable Assets Statistic"
            marketAverage={assetsStatistic}
            currency={true}
          />
        </div>
        <div className="form-page-total-row">
          <span className="form-page-total-label">Total Investable Assets</span>
          <span id="total" className="form-page-total-value-blue">
            ${totalInvestableAssets.toLocaleString()}
          </span>
        </div>
      </div>
    </div>
  );
}
